/* global gtag_report_conversion */
import React, { useState } from "react";
import DOMPurify from "dompurify";
import Swal from "sweetalert2";
import "../styles/contact.css";
import "../styles/common.css";
import { FaFacebook, FaClock } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";

const ContactSection = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    carType: "", // NEW
    vin: "", // NEW
    message: "",
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "vin") {
      // normalize VIN: remove spaces/dashes, uppercase
      const cleaned = value.replace(/[\s-]/g, "").toUpperCase();
      setForm({ ...form, vin: cleaned });
    } else {
      setForm({ ...form, [id]: value });
    }
  };

  const sanitizeForm = () => {
    return {
      name: DOMPurify.sanitize(form.name.trim()),
      phone: DOMPurify.sanitize(form.phone.trim()),
      email: DOMPurify.sanitize(form.email.trim()),
      carType: DOMPurify.sanitize(form.carType.trim()),
      vin: DOMPurify.sanitize(form.vin.trim().toUpperCase()),
      message: DOMPurify.sanitize(form.message.trim()),
    };
  };

  const validateForm = ({ name, phone, email, carType, vin, message }) => {
    if (name.length < 2) return "A név túl rövid.";
    if (!/^\+?[0-9\s\-]{6,}$/.test(phone)) return "Érvénytelen telefonszám.";
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email))
      return "Érvénytelen email cím.";
    if (carType.length < 2)
      return "Kérlek add meg az autó típusát (márka–modell).";
    // VIN: 17 chars, no I/O/Q
    if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin))
      return "Érvénytelen alvázszám (VIN). 17 karakter, csak számok és nagybetűk (I, O, Q nélkül).";
    if (message.length < 10)
      return "Az üzenet túl rövid, kérlek részletesen írd le milyen munkáról lenne szó.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sanitizedData = sanitizeForm();
    const validationError = validateForm(sanitizedData);

    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Hibás űrlapadat",
        text: validationError,
        confirmButtonColor: "#d33",
      });
      return;
    }

    try {
      const response = await fetch(
        "https://a53tol5cgh.execute-api.eu-north-1.amazonaws.com/prod/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(sanitizedData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (typeof window.gtag === "function") {
          window.gtag("event", "form_submission", {
            event_category: "Contact",
            event_label: "Contact Form - Olajcsereszolnok.hu",
          });
          window.gtag("event", "conversion", {
            send_to: "AW-17463012061/0ZSWCITi14QbEN3VgYdB",
          });
        }
        Swal.fire({
          icon: "success",
          title: "Üzenet elküldve!",
          text: "Köszönjük, néhány órán belül felvesszük Önnel a kapcsolatot.",
          confirmButtonColor: "#fdac21",
        });
        setForm({
          name: "",
          phone: "",
          email: "",
          carType: "",
          vin: "",
          message: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Hiba!",
          text: result.error || "Valami hiba történt a küldés során.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Szerverhiba",
        text: "Nem sikerült csatlakozni a szerverhez.",
        confirmButtonColor: "#d33",
      });
    }
  };

  return (
    <section className="contact-section py-5" id="contact">
      <div className="container">
        <h2 className="h2-header mb-3 text-center">
          Lépj velünk kapcsolatba – gyors időpontfoglalás Szolnokon
        </h2>

        <div className="divider">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <p className="services-intro text-center mb-5">
          Szeretnél olajcserére, fékcserére vagy állapotfelmérésre időpontot
          kérni? Töltsd ki az alábbi űrlapot, és szolnoki szervizünk munkatársa
          rövid időn belül visszahív, hogy egyeztessük a részleteket.
          <br />
          <br />
          Foglalj most – hogy autód megbízhatóan működjön!
        </p>

        {/* Form */}
        <div className="card p-4 shadow-sm contact-form-card mx-auto mb-5">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">
                Név
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                required
                placeholder="Add meg a neved."
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="phone" className="form-label">
                Telefonszám
              </label>
              <input
                type="tel"
                className="form-control"
                id="phone"
                required
                placeholder="+36-30-123-4567"
                value={form.phone}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email cím
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                required
                placeholder="email@cím.hu"
                value={form.email}
                onChange={handleChange}
              />
            </div>

            {/* NEW: Car type */}
            <div className="mb-3">
              <label htmlFor="carType" className="form-label">
                Autó típusa (márka–modell / évjárat)
              </label>
              <input
                type="text"
                className="form-control"
                id="carType"
                required
                placeholder="Pl. Toyota Corolla 1.6 (2008)"
                value={form.carType}
                onChange={handleChange}
              />
            </div>

            {/* NEW: VIN */}
            <div className="mb-3">
              <label htmlFor="vin" className="form-label">
                Alvázszám (VIN)
              </label>
              <input
                type="text"
                className="form-control"
                id="vin"
                required
                inputMode="latin"
                maxLength={17}
                placeholder="Pl. VF1ABCD1234567890"
                value={form.vin}
                onChange={handleChange}
                aria-describedby="vinHelp"
              />
              <small id="vinHelp" className="form-text">
                Az alvázszám (VIN) a{" "}
                <strong>forgalmi engedély E. mezőjében</strong> található. 17
                karakter, csak számok és nagybetűk.
              </small>
            </div>

            <div className="mb-3">
              <label htmlFor="message" className="form-label">
                Munka leírása
              </label>
              <textarea
                className="form-control"
                id="message"
                rows="4"
                required
                placeholder="Írd le, miben segíthetünk pár mondatban"
                value={form.message}
                onChange={handleChange}
              ></textarea>
            </div>

            <button type="submit" className="contact-btn">
              Küldés
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
